<script setup lang="ts">
/**
 * DeleteUserModal — confirmation modal for `DELETE /api/cms/users/{userName}`.
 *
 * Renders with `kind="danger"` for prominent red accent. Requires
 * the admin to type the userName for confirmation — guards against
 * accidental clicks. (Common pattern for destructive actions; we'll
 * reuse this in Sprint 2 batches B and C for bundle/category
 * deletes.)
 */
import { ref, computed, watch } from 'vue'
import { useI18n } from 'vue-i18n'
import { useToastStore } from '@/stores/toast'
import Modal from '@/components/ui/Modal.vue'
import Button from '@/components/ui/Button.vue'
import Input from '@/components/ui/Input.vue'
import FormRow from '@/components/ui/FormRow.vue'
import { ApiException } from '@/api/types'
import { useDeleteUserMutation } from '../queries'
import type { UserView } from '../api'

interface Props {
  user: UserView | null
}
const props = defineProps<Props>()

const open = defineModel<boolean>('open', { required: true })

const { t } = useI18n()
const toast = useToastStore()

const userName = computed(() => props.user?.userName ?? '')
const del = useDeleteUserMutation(userName.value)

const confirmInput = ref('')
const isSubmitting = ref(false)

const canConfirm = computed(
  () => confirmInput.value.trim() === userName.value && userName.value !== '',
)

// Reset the confirmation field whenever the modal closes.
watch(open, (isOpen) => {
  if (!isOpen) confirmInput.value = ''
})

async function onSubmit(): Promise<void> {
  if (!canConfirm.value) return
  isSubmitting.value = true
  try {
    await del.mutateAsync()
    toast.push({
      kind: 'success',
      message: t('iam.delete.success', { name: userName.value }),
    })
    open.value = false
  } catch (err) {
    if (err instanceof ApiException) {
      // Most likely 409 if the user has FK references that block
      // deletion. The mutation wrapper already surfaced the toast.
      // We just stay open so the admin can read the error and decide.
    } else {
      throw err
    }
  } finally {
    isSubmitting.value = false
  }
}

function close(): void {
  open.value = false
}
</script>

<template>
  <Modal
    v-model:open="open"
    kind="danger"
    :title="$t('iam.delete.title', { name: userName })"
    :description="$t('iam.delete.description')"
    size="md"
  >
    <div v-if="user" class="space-y-3">
      <p class="text-sm text-text-2 m-0">
        {{ $t('iam.delete.warning') }}
      </p>

      <FormRow
        :label="$t('iam.delete.confirm.label', { name: userName })"
        :helper="$t('iam.delete.confirm.helper')"
      >
        <Input
          v-model="confirmInput"
          type="text"
          :placeholder="userName"
          autocomplete="off"
        />
      </FormRow>
    </div>

    <template #footer>
      <Button kind="ghost" @click="close">
        {{ $t('actions.cancel') }}
      </Button>
      <Button
        kind="danger"
        :disabled="!canConfirm"
        :loading="isSubmitting"
        @click="onSubmit"
      >
        {{ $t('iam.delete.submit') }}
      </Button>
    </template>
  </Modal>
</template>
